import React, {useRef, useCallback, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Text, Box, Spinner, Center, useDisclosure} from '@chakra-ui/react'
import useSWRInfinite from 'swr/infinite'
import {useTranslation} from 'react-i18next'

import DataTable from '../../../../components/DataTable'
import InvoiceDrawer from '../InvoiceDrawer'
import {fetcher} from '../../../../../api'
import {camelizeResult, dineroMoneyObjects} from '../../../../../api/serializer'
import {columns} from './columns'

const InvoiceTable = ({payerId, status, payable, perPage}) => {
  const {t} = useTranslation('payerStatements')

  const loadingRef = useRef(null)

  const getKey = (pageIndex) => {
    return `/api/payer_statements/customers/${payerId}/invoices?status=${status}&per_page=${perPage}&page=${
      pageIndex + 1
    }`
  }

  const initialData = {
    invoices: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      nextPage: null,
    },
  }

  const {data, size, setSize, isLoading, isValidating} = useSWRInfinite(
    getKey,
    fetcher,
    {
      use: [camelizeResult, dineroMoneyObjects],
      fallbackData: [initialData],
    },
  )

  useEffect(() => {
    setSize(1)
  }, [setSize, status])

  const invoices = Object.keys(data).reduce(
    (acc, key) => [...acc, ...data[key].invoices],
    [],
  )

  const invoiceDrawer = useDisclosure()
  const [selectedInvoice, setSelectedInvoice] = React.useState(null)
  const showInvoice = (invoice) => {
    if (!invoice) return

    setSelectedInvoice(invoice)
    invoiceDrawer.onOpen()
  }

  const pagination = data[size - 1]?.pagination || initialData.pagination

  const isLoadingMore = isValidating || pagination.nextPage

  const showNoMoreToLoadMessage = pagination.nextPage === null && size > 1

  const loadMoreInvoices = useCallback(() => {
    if (isValidating) {
      return
    }

    if (!pagination.nextPage) {
      return
    }

    setSize(size + 1)
  }, [pagination.nextPage, isValidating, setSize, size])

  useEffect(() => {
    const currentLoadingRef = loadingRef.current

    if (!currentLoadingRef) {
      return () => {}
    }

    if (!pagination.nextPage) {
      return () => {}
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreInvoices()
        }
      },
      {threshold: 0.5},
    )

    observer.observe(currentLoadingRef)

    return () => observer.unobserve(currentLoadingRef)
  }, [loadingRef, pagination.nextPage, loadMoreInvoices])

  return (
    <Box>
      <InvoiceDrawer
        isOpen={invoiceDrawer.isOpen}
        onClose={invoiceDrawer.onClose}
        invoice={selectedInvoice}
      />
      <Box overflowX={{base: 'scroll', md: 'auto'}} pb="md">
        <DataTable
          chakraTableProps={{variant: 'statement'}}
          columns={columns(t, payable)}
          data={invoices}
          isLoading={isLoading && size === 1}
          onClickRow={showInvoice}
        />
      </Box>
      <Box>
        {isLoadingMore && (
          <Center p={4} ref={loadingRef}>
            <Spinner size="md" color="blue.500" />
          </Center>
        )}

        {showNoMoreToLoadMessage && (
          <Center p={4}>
            <Text>{t('invoices.table.noMoreToLoadMessage')}</Text>
          </Center>
        )}
      </Box>
    </Box>
  )
}

InvoiceTable.propTypes = {
  payerId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  payable: PropTypes.bool.isRequired,
  perPage: PropTypes.number.isRequired,
}

export default InvoiceTable
